import { useState, useEffect } from 'react';
import { getAuditLog, getUsers } from '../lib/api';
import { formatApiError } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DatePicker } from '../components/ui/date-picker';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ClipboardText, Phone, UserCircle, Handshake, ChatCircleDots, UsersThree, CheckCircle, ArrowRight } from '@phosphor-icons/react';

const TYPE_ICONS = {
  activity: Phone,
  client_created: UserCircle,
  lead_created: UsersThree,
  task_created: ClipboardText,
  followup_created: ArrowRight,
};

const TYPE_COLORS = {
  activity: 'bg-green-100 text-green-700',
  client_created: 'bg-blue-100 text-blue-700',
  lead_created: 'bg-purple-100 text-purple-700',
  task_created: 'bg-amber-100 text-amber-700',
  followup_created: 'bg-rose-100 text-rose-700',
};

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    getUsers().then(res => setUsers(res.data)).catch(() => {});
  }, []);

  const fetchLog = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedUser) params.user_id = selectedUser;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getAuditLog(params);
      setEntries(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, []);

  const userMap = {};
  users.forEach(u => { userMap[u.id] = u.name; });

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return ts; }
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Activity Audit Log
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Track employee CRM activity across the system
        </p>
      </div>

      {/* Filters */}
      <Card className="border-zinc-200 rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1 min-w-[200px]">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Employee</Label>
              <SearchableSelect
                value={selectedUser}
                onValueChange={setSelectedUser}
                options={[{ value: '', label: 'All Employees' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                placeholder="Select employee"
                data-testid="audit-user-filter"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">From</Label>
              <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From date" className="w-[160px]" data-testid="audit-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">To</Label>
              <DatePicker value={dateTo} onChange={setDateTo} placeholder="To date" className="w-[160px]" data-testid="audit-date-to" />
            </div>
            <Button onClick={fetchLog} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="audit-apply-btn">
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardText size={48} className="mx-auto text-zinc-300 mb-4" />
          <p className="text-zinc-500">No activity found for the selected filters</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-zinc-500">{entries.length} entries</p>
          <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
            {entries.map((entry, idx) => {
              const Icon = TYPE_ICONS[entry.type] || ClipboardText;
              const colorClass = TYPE_COLORS[entry.type] || 'bg-zinc-100 text-zinc-700';
              const details = entry.details || {};
              return (
                <div key={idx} className="p-4 flex items-start gap-4 hover:bg-zinc-50/50 transition-colors" data-testid={`audit-entry-${idx}`}>
                  <div className={`p-2 rounded-sm ${colorClass}`}>
                    <Icon size={18} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{entry.summary}</p>
                      <Badge variant="outline" className="text-[10px]">{entry.subtype}</Badge>
                      {entry.type === 'activity' && details.call_status && (
                        <Badge className={`text-[10px] ${details.call_status === 'Answered' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}>
                          {details.call_status}
                        </Badge>
                      )}
                      {entry.type === 'activity' && details.meeting_status && (
                        <Badge className={`text-[10px] ${details.meeting_status === 'Met' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}>
                          {details.meeting_status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      by <span className="font-medium text-zinc-700">{userMap[entry.user_id] || entry.user_id}</span>
                      {' '}&middot;{' '}{formatTimestamp(entry.timestamp)}
                    </p>
                    {details.description && <p className="text-xs text-zinc-400 mt-1 truncate">{details.description}</p>}
                    {details.call_duration && <p className="text-xs text-zinc-400 mt-0.5">Duration: {details.call_duration}</p>}
                    {details.meeting_duration && <p className="text-xs text-zinc-400 mt-0.5">Duration: {details.meeting_duration}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
